import { useMemo } from "react";
import { useApiClient } from "@src/packages/api/louise-api";
import { useIsAuthReady } from "@src/packages/api/useStytch";
import { useExperienceContext } from "./useExperienceContext";
import {
  resolveAuthorizedProjectionStatus,
  type AuthorizedProjectionStatus,
} from "../resolveAuthorizedProjectionStatus";
import {
  resolveCanonicalExperienceContext,
  type ResolvedCanonicalExperienceContext,
} from "../resolveCanonicalExperienceContext";
import type { CanonicalExperienceContext } from "../canonicalExperienceContext";

export type AuthorizedExperienceProjection = {
  status: AuthorizedProjectionStatus;
  canonical: CanonicalExperienceContext | null;
  projection: ResolvedCanonicalExperienceContext | null;
  error: Error | null;
};

/**
 * The single consumer-facing read model for journey-defining state.
 * Consumers should use the canonical projection when it is available, while
 * keeping user-entered data and editable flows available when it is absent or
 * temporarily unavailable.
 *
 * The status comes from a presenter rather than from `query.isLoading`, because
 * a disabled query and a settled empty one look identical through that field —
 * see `resolveAuthorizedProjectionStatus` for what that cost.
 */
export function useAuthorizedExperienceProjection(): AuthorizedExperienceProjection {
  const isAuthReady = useIsAuthReady();
  const { patientId } = useApiClient();
  const query = useExperienceContext();

  const status = resolveAuthorizedProjectionStatus({
    isAuthReady,
    hasPatient: !!patientId,
    isSettled: query.isSuccess || query.isError,
    isPaused: query.isPaused,
    hasError: !!query.error,
    hasData: !!query.data,
  });

  return useMemo(() => {
    if (status === "loading") {
      return {
        status,
        canonical: null,
        projection: null,
        error: null,
      };
    }

    if (status === "error") {
      return {
        status,
        canonical: null,
        projection: null,
        error:
          query.error instanceof Error
            ? query.error
            : new Error(String(query.error)),
      };
    }

    if (status === "absent" || !query.data) {
      return {
        status: "absent",
        canonical: null,
        projection: null,
        error: null,
      };
    }

    return {
      status: "ready",
      canonical: query.data,
      projection: resolveCanonicalExperienceContext(query.data),
      error: null,
    };
  }, [status, query.data, query.error]);
}
